/**
 * useSimilarRecords — debounced "similar past records" suggestions for
 * create pages.
 *
 * const { suggestions, loading, unsupported } =
 *   useSimilarRecords('ptw', formData.work_description, { enabled });
 *
 * - fires only when query.trim().length >= minLength (default 15)
 * - 600ms debounce, aborts superseded requests
 * - feature-detects an absent backend (404/501) and goes silent for the
 *   rest of the mount (`unsupported`) — panels simply don't render
 * - all failures degrade to empty suggestions; never throws into the page
 */

import { useEffect, useRef, useState } from 'react';
import { fetchSimilar } from '../services/assistantClient';

export default function useSimilarRecords(
  module,
  query,
  { enabled = true, minLength = 15, debounceMs = 600, limit = 5 } = {}
) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const abortRef = useRef(null);
  const lastQueryRef = useRef('');

  useEffect(() => {
    const q = (query || '').trim();
    if (!enabled || unsupported || q.length < minLength) {
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }
    if (q === lastQueryRef.current) return undefined;

    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const data = await fetchSimilar(module, q, { signal: controller.signal, limit });
        lastQueryRef.current = q;
        setSuggestions(data?.results || []);
      } catch (err) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        const status = err?.response?.status;
        if (status === 404 || status === 501) setUnsupported(true);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [module, query, enabled, unsupported, minLength, debounceMs, limit]);

  useEffect(() => () => abortRef.current?.abort(), []);

  return { suggestions, loading, unsupported };
}
